import React, { useState, useEffect, useRef, useCallback, KeyboardEvent } from 'react';
import * as signalR from '@microsoft/signalr';
import { getAccessToken } from '../auth/AuthContext';
import { useAuth } from '../auth/AuthContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../api/client';
import type { VaultMessageDto, ChatHistoryResponse } from '../types/api';
import { formatChatTime } from '../utils/dateUtils';
import './VaultChatPanel.css';

interface VaultChatPanelProps {
  vaultId: string;
  vaultName: string;
  onClose?: () => void;
  onUnreadChange: (count: number) => void;
  inline?: boolean;
}

export default function VaultChatPanel({
  vaultId,
  vaultName,
  onClose,
  onUnreadChange,
  inline = false,
}: VaultChatPanelProps) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<VaultMessageDto[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const panelOpenRef = useRef(true);

  // Scroll to bottom when new messages arrive (only if already near bottom)
  const scrollToBottom = useCallback((force = false) => {
    if (!messagesEndRef.current) return;
    const container = messagesEndRef.current.parentElement;
    if (!container) return;
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    if (force || nearBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Load initial history and mark as read
  useEffect(() => {
    panelOpenRef.current = true;

    async function init() {
      try {
        const data = await apiGet<ChatHistoryResponse>(
          `/api/vault/${vaultId}/chat/messages?pageSize=40`
        );
        setMessages(data.messages);
        setHasMore(data.hasMore);

        // Mark vault chat as read
        await apiPut(`/api/vault/${vaultId}/chat/read`);
        onUnreadChange(0);
      } catch {
        // non-critical
      } finally {
        setLoadingHistory(false);
      }
    }

    init();

    return () => {
      panelOpenRef.current = false;
    };
  }, [vaultId, onUnreadChange]);

  // Scroll to bottom after initial load
  useEffect(() => {
    if (!loadingHistory) {
      scrollToBottom(true);
    }
  }, [loadingHistory, scrollToBottom]);

  // SignalR connection
  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/vaultchat', {
        accessTokenFactory: getAccessToken as () => string | Promise<string>,
      })
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    connection.on('ReceiveMessage', (msg: VaultMessageDto) => {
      // Deduplicate: sender already added their own message optimistically in handleSend
      setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
      if (panelOpenRef.current) {
        // Mark read immediately if panel is open
        apiPut(`/api/vault/${vaultId}/chat/read`).catch(() => {});
        onUnreadChange(0);
      }
    });

    connection
      .start()
      .then(() => connection.invoke('JoinVault', vaultId))
      .catch(() => {
        // SignalR connection failure is non-fatal — REST API still works
      });

    return () => {
      connection.invoke('LeaveVault', vaultId).catch(() => {});
      connection.stop();
    };
  }, [vaultId, onUnreadChange]);

  async function loadMore() {
    if (!hasMore || loadingMore || messages.length === 0) return;
    setLoadingMore(true);
    try {
      const oldest = messages[0].sentAt;
      const data = await apiGet<ChatHistoryResponse>(
        `/api/vault/${vaultId}/chat/messages?pageSize=40&before=${encodeURIComponent(oldest)}`
      );
      setMessages(prev => [...data.messages, ...prev]);
      setHasMore(data.hasMore);
    } catch {
      // non-critical
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleSend() {
    const content = input.trim();
    if (!content || sending) return;

    setSending(true);
    setInput('');
    try {
      const sent = await apiPost<VaultMessageDto>(`/api/vault/${vaultId}/chat/messages`, { content });
      // Optimistically show sender's own message immediately; SignalR may also deliver
      // it (deduped by ID in ReceiveMessage handler) but cannot be relied on in production
      setMessages(prev => prev.some(m => m.id === sent.id) ? prev : [...prev, sent]);
      scrollToBottom(true);
    } catch {
      setInput(content); // restore on failure
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function handleDelete(messageId: string) {
    try {
      await apiDelete(`/api/vault/${vaultId}/chat/messages/${messageId}`);
      // Optimistically remove from UI (server soft-deletes, won't appear on reload)
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch {
      // non-critical
    }
  }

  function formatTime(sentAt: string) {
    return formatChatTime(sentAt);
  }

  // Derive current user ID by matching email against sent messages isn't clean;
  // instead we use sentByName matching — but that's fragile. Use sentByUserId is better.
  // We'll mark "mine" by comparing sentByName to user's full name.
  // (The user object doesn't carry userId, but sentByUserId is in the DTO.)
  // We need to know our own userId. Since we don't have it in AuthContext,
  // we fall back to comparing the sender name with the current user's name.
  function isOwnMessage(msg: VaultMessageDto) {
    if (!user) return false;
    return msg.sentByName === `${user.firstName} ${user.lastName}`;
  }

  return (
    <div className={inline ? 'chat-inline' : 'chat-panel'} role={inline ? undefined : 'dialog'} aria-label="Vault Chat">
      {!inline && (
        <div className="chat-panel-header">
          <div className="chat-panel-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            {vaultName}
          </div>
          <button className="chat-panel-close" onClick={onClose} aria-label="Close chat">
            &times;
          </button>
        </div>
      )}

      <div className="chat-panel-messages" onScroll={e => {
        // Load more when scrolled to top
        if ((e.currentTarget as HTMLElement).scrollTop < 40) {
          loadMore();
        }
      }}>
        <div ref={messagesTopRef} />

        {loadingMore && (
          <div className="chat-loading-more">Loading earlier messages…</div>
        )}

        {hasMore && !loadingMore && (
          <button className="chat-load-more" onClick={loadMore}>
            Load earlier messages
          </button>
        )}

        {loadingHistory ? (
          <div className="chat-loading">Loading messages…</div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">
            <p>Be the first to start a conversation.</p>
            <p className="chat-empty-sub">Messages are visible to all vault members.</p>
          </div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              className={`chat-bubble-row${isOwnMessage(msg) ? ' chat-bubble-row--own' : ''}`}
            >
              <div className="chat-bubble">
                {!isOwnMessage(msg) && (
                  <div className="chat-sender">{msg.sentByName}</div>
                )}
                <div className="chat-content">{msg.content}</div>
                <div className="chat-meta">
                  <span className="chat-time">{formatTime(msg.sentAt)}</span>
                  {isOwnMessage(msg) && (
                    <button
                      className="chat-delete-btn"
                      onClick={() => handleDelete(msg.id)}
                      aria-label="Delete message"
                      title="Delete"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-panel-input-row">
        <textarea
          className="chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message… (Enter to send)"
          rows={2}
          maxLength={4000}
          disabled={sending}
          aria-label="Message input"
        />
        <button
          className="chat-send-btn"
          onClick={handleSend}
          disabled={!input.trim() || sending}
          aria-label="Send message"
        >
          {sending ? '…' : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
